import React, { useMemo } from "react";
import { useTableAutoRefresh } from "../../hooks/useTableAutoRefresh";
import { MaterialReactTable } from "material-react-table";
import { useTheme } from "@mui/material/styles";
import { sharedTableProps } from "../shared/tableProps";
import Tooltip from "@mui/material/Tooltip";
import "../util/tables.css";
import { useDataView } from "../../contexts/data.view.context";
import ShowLoadingBar from "../util/showLoadingBar";
import { getGroupAccent, ToolbarStat, ToolbarLegend, ToolbarRow } from "../shared/monitoringStyles";

const EVENT_COUNTS_GROUP_INDEX = 0;

const CenteredCell = ({ value, style = {} }) => (
  <span
    style={{
      display: "block",
      textAlign: value == null || value === "" ? "center" : "left",
      ...style,
    }}
  >
    {value == null || value === "" ? "-" : value}
  </span>
);

function getColumns(theme) {
  return [
  {
    accessorKey: "Source",
    header: "Source",
    id: "Source",
    size: 120,
  },
  {
    accessorKey: "Entity",
    header: "Entity",
    id: "Entity",
    size: 120,
  },
  {
    accessorKey: "LastDateReceived",
    header: "Last Date Received",
    id: "LastDateReceived",
    size: 150,
    Cell: ({ cell }) => <CenteredCell value={cell.getValue()} />,
  },
  {
    accessorKey: "DaysNoDataReceived",
    header: "Days No Data",
    id: "DaysNoDataReceived",
    size: 80,
    Cell: ({ cell }) => <CenteredCell value={cell.getValue()} />,
  },
  {
    accessorKey: "LastFilesReceived",
    header: "Last File(s) Received",
    id: "LastFilesReceived",
    size: 200,
    Cell: ({ cell }) => {
      const value = cell.getValue();
      if (!value) {
        return <span style={{ display: "block", textAlign: "center" }}>-</span>;
      }
      const files = value.split(",").filter(Boolean);
      const displayText =
        files.length > 1 ? `${files[0]} (+${files.length - 1} more)` : files[0];
      const tooltipText = files.map((f, i) => `${i + 1}. ${f}`).join("\n");

      return (
        <Tooltip
          title={
            <span
              style={{
                whiteSpace: "pre-line",
                fontSize: "0.78rem",
                lineHeight: 1.8,
              }}
            >
              {tooltipText}
            </span>
          }
          arrow
          placement="right"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "#2c2c2c",
                color: "#fff",
                fontSize: "0.78rem",
                borderRadius: "6px",
                padding: "8px 12px",
                maxWidth: 420,
                boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              },
            },
            arrow: {
              sx: { color: "#2c2c2c" },
            },
          }}
        >
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "block",
              maxWidth: 200,
              cursor: "help",
              textDecoration:
                files.length > 1 ? "underline dotted #888" : "none",
            }}
          >
            {displayText}
          </span>
        </Tooltip>
      );
    },
  },

  {
    header: "Event Counts",
    columns: [
      {
        header: "T",
        accessorKey: "EventCountToday",
        id: "EventCountToday",
        size: 60,
        Cell: ({ cell }) => <CenteredCell value={cell.getValue()} />,
      },
      {
        header: "Y",
        accessorKey: "EventCountYesterday",
        id: "EventCountYesterday",
        size: 60,
        Cell: ({ cell }) => <CenteredCell value={cell.getValue()} />,
      },
      {
        header: "7D",
        accessorKey: "EventCountLast7Days",
        id: "EventCountLast7Days",
        size: 65,
        Cell: ({ cell }) => <CenteredCell value={cell.getValue()} />,
      },
      {
        header: "30D",
        accessorKey: "EventCountLast30Days",
        id: "EventCountLast30Days",
        size: 70,
        Cell: ({ cell }) => <CenteredCell value={cell.getValue()} />,
      },
    ],
  },

  {
    accessorKey: "LastIntegrationErrorDate",
    header: "Last Error Date",
    id: "LastIntegrationErrorDate",
    size: 150,
    Cell: ({ cell }) => (
      <CenteredCell
        value={cell.getValue()}
        style={{ color: cell.getValue() ? theme.palette.error.main : "inherit" }}
      />
    ),
  },
  {
    accessorKey: "LastIntegrationError",
    header: "Last Error Message",
    id: "LastIntegrationError",
    size: 220,
    Cell: ({ cell }) => {
      const value = cell.getValue();
      if (!value) {
        return <span style={{ display: "block", textAlign: "center" }}>-</span>;
      }
      return (
        <Tooltip
          title={
            <span style={{ fontSize: "0.78rem", lineHeight: 1.8 }}>
              {value}
            </span>
          }
          arrow
          placement="right"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "#2c2c2c",
                color: "#fff",
                fontSize: "0.78rem",
                borderRadius: "6px",
                padding: "8px 12px",
                maxWidth: 420,
                boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              },
            },
            arrow: {
              sx: { color: "#2c2c2c" },
            },
          }}
        >
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "block",
              maxWidth: 220,
              color: theme.palette.error.main,
              cursor: "help",
            }}
          >
            {value}
          </span>
        </Tooltip>
      );
    },
  },
  ];
}

const UnifiedBatchProcessSummary = () => {
  const theme = useTheme();
  const columns = useMemo(() => getColumns(theme), [theme]);
  const { fetchBatchProcessSummary, batchProcessSummary } = useDataView();
  let loading = false;
  const tableMaxHeight = useTableAutoRefresh(fetchBatchProcessSummary, 3_600_000);

  if (batchProcessSummary.length === 0) {
    loading = true;
  }

  const totalEventsToday = batchProcessSummary.reduce(
    (sum, row) => sum + (Number(row.EventCountToday) || 0),
    0,
  );

  const totalErrorsToday = batchProcessSummary.filter(
    (row) =>
      row.LastIntegrationErrorDate &&
      new Date(row.LastIntegrationErrorDate).toDateString() ===
        new Date().toDateString(),
  ).length;

  const allEntries = Array.from(batchProcessSummary.values());
  const lastRefreshedTime = allEntries.reduce((latest, entry) => {
    if (!entry?.InsertDate) return latest;
    return !latest || new Date(entry.InsertDate) > new Date(latest)
      ? entry.InsertDate
      : latest;
  }, null);

  const localDateString = lastRefreshedTime
    ? new Date(lastRefreshedTime).toLocaleString("en-US", {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timeZoneName: "short",
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : "N/A";

  if (Boolean(loading)) {
    return <ShowLoadingBar title="Batch Process Summary" loading={loading} />;
  }

  return (
    <div className="relative">
      {batchProcessSummary.length > 0 && (
        <MaterialReactTable
          {...sharedTableProps}
          columns={columns}
          data={batchProcessSummary.map((row) => ({
            Source: row.Source,
            Entity: row.Entity,
            LastDateReceived: row.LastDateReceived,
            DaysNoDataReceived: row.DaysNoDataReceived,
            LastFilesReceived: row.LastFilesReceived,
            EventCountToday: row.EventCountToday,
            EventCountYesterday: row.EventCountYesterday,
            EventCountLast7Days: row.EventCountLast7Days,
            EventCountLast30Days: row.EventCountLast30Days,
            LastIntegrationErrorDate: row.LastIntegrationErrorDate,
            LastIntegrationError: row.LastIntegrationError,
            InsertDate: row.InsertDate,
          }))}
          enablePinning
          initialState={{
            density: "compact",
            pagination: { pageSize: 100 },
            sorting: [{ id: "Source", desc: false }],
            columnPinning: { left: ["Source", "Entity"] },
          }}
          autoResetPageIndex={false}
          renderTopToolbarCustomActions={() => (
            <ToolbarRow>
              <ToolbarStat label="Last Refreshed" value={localDateString} color="primary.main" />
              <ToolbarStat label="Events Today" value={totalEventsToday} color="success.main" />
              <ToolbarStat
                label="Sources with Errors Today"
                value={totalErrorsToday}
                color={totalErrorsToday > 0 ? "error.main" : "success.main"}
              />
              <ToolbarLegend />
            </ToolbarRow>
          )}
          muiTopToolbarProps={{ sx: { mt: 3 } }}
          muiTableBodyCellProps={({ column }) => {
            const dividerBorder = `3px solid ${theme.palette.primary.main}`;
            return {
              sx: {
                borderBottom: "1px solid",
                borderColor: "divider",
                padding: "6px 10px",
                fontSize: "0.8rem",
                // Source/Entity are pinned to a fixed width — wrap long
                // values onto multiple lines instead of clipping them.
                ...((column.id === "Source" || column.id === "Entity") && {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.3,
                }),
                borderLeft: column.id === "EventCountToday" ? dividerBorder : undefined,
                borderRight: column.id === "EventCountLast30Days" ? dividerBorder : undefined,
              },
            };
          }}
          muiTableHeadCellProps={({ column }) => {
            const accent = getGroupAccent(theme, EVENT_COUNTS_GROUP_INDEX);
            const dividerBorder = `3px solid ${theme.palette.primary.main}`;
            return {
              sx: {
                backgroundColor: column.header === "Event Counts" ? accent.headerBg : "#F8FAFC",
                color: "text.secondary",
                fontWeight: 700,
                fontSize: "0.72rem",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                textAlign: "center",
                borderBottom: "2px solid",
                borderColor: "divider",
                "& .Mui-TableHeadCell-Content": { justifyContent: "center" },
                // MaterialReactTable forces white-space:nowrap on header
                // labels under 20 characters, truncating with an ellipsis
                // regardless of column width — force wrapping back on.
                "& .Mui-TableHeadCell-Content-Wrapper": {
                  whiteSpace: "normal",
                  overflow: "visible",
                },
                borderLeft: column.id === "EventCountToday" ? dividerBorder : undefined,
                borderRight: column.id === "EventCountLast30Days" ? dividerBorder : undefined,
              },
            };
          }}
          muiTableBodyRowProps={{
            sx: {
              "&:nth-of-type(even) td": { backgroundColor: "#FAFBFC" },
              "&:hover td": { backgroundColor: "action.hover" },
            },
          }}
          muiTableContainerProps={{ sx: { maxHeight: tableMaxHeight } }}
          muiTableHeadProps={{
            sx: {
              position: "sticky",
              top: 0,
              zIndex: 2,
              backgroundColor: "background.paper",
            },
          }}
        />
      )}
    </div>
  );
};

export default UnifiedBatchProcessSummary;
